/**
 * PuppyAdGenerator - AI-powered puppy listing generator for breeders
 * Generates: FB post, OLX listing, CTA, hashtags, litter description
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Copy, CheckCircle2, Facebook,
  MapPin, Calendar, Hash, FileText, Megaphone, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PanelLayout from '@/components/PanelLayout';
import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success(`${label} skopiowany!`);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={handleCopy}
      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors hover:bg-gray-100"
      style={{ color: copied ? '#10B981' : 'var(--apisai-text-secondary)', border: '1px solid var(--apisai-border)' }}
      data-testid={`copy-${label.toLowerCase()}`}>
      {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Skopiowano' : 'Kopiuj'}
    </button>
  );
}

function ResultSection({ icon: Icon, title, content, iconColor, label }) {
  if (!content) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border p-4" style={{ background: 'white', borderColor: 'var(--apisai-border)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: iconColor + '15', color: iconColor }}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--apisai-text)' }}>{title}</h3>
        </div>
        <CopyButton text={content} label={label} />
      </div>
      <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--apisai-text)' }}>{content}</p>
    </motion.div>
  );
}

export default function PuppyAdGenerator() {
  const { t } = useTranslation();
  const [breed, setBreed] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [city, setCity] = useState('');
  const [puppyCount, setPuppyCount] = useState(5);
  const [parents, setParents] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const token = localStorage.getItem('token');

  const handleGenerate = async () => {
    if (!breed.trim()) { toast.error('Podaj rasę'); return; }
    if (!city.trim()) { toast.error('Podaj miasto'); return; }

    setGenerating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/booster/puppy-ad/generate`, {
        breed, birth_date: birthDate, city, puppy_count: puppyCount,
        parents, additional_info: additionalInfo,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data.ad);
      toast.success('Ogłoszenie wygenerowane!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd generowania');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <PanelLayout>
      <div className="max-w-3xl mx-auto" data-testid="puppy-ad-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#EC4899' }}>
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--apisai-text, #0F172A)' }}>Ogłoszenie szczeniąt</h1>
              <p className="text-sm" style={{ color: 'var(--apisai-text-secondary, #64748B)' }}>
                AI wygeneruje profesjonalne ogłoszenie w 10 sekund
              </p>
            </div>
          </div>
        </motion.div>

        {/* Form */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border p-5 mb-5" style={{ background: 'white', borderColor: 'var(--apisai-border)' }}
          data-testid="puppy-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--apisai-text-secondary)' }}>Rasa *</label>
              <input value={breed} onChange={(e) => setBreed(e.target.value)}
                placeholder="np. Owczarek Niemiecki"
                className="w-full rounded-xl border px-3 py-2.5 text-sm"
                style={{ borderColor: 'var(--apisai-border)' }}
                data-testid="breed-input" />
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--apisai-text-secondary)' }}>Miasto *</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--apisai-text-muted)' }} />
                <input value={city} onChange={(e) => setCity(e.target.value)}
                  placeholder="np. Warszawa"
                  className="w-full rounded-xl border pl-9 pr-3 py-2.5 text-sm"
                  style={{ borderColor: 'var(--apisai-border)' }}
                  data-testid="city-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--apisai-text-secondary)' }}>Data urodzenia</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--apisai-text-muted)' }} />
                <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)}
                  className="w-full rounded-xl border pl-9 pr-3 py-2.5 text-sm"
                  style={{ borderColor: 'var(--apisai-border)' }}
                  data-testid="birthdate-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--apisai-text-secondary)' }}>Liczba szczeniąt</label>
              <div className="flex gap-1.5" data-testid="puppy-count">
                {[2, 3, 4, 5, 6, 7, 8].map(n => (
                  <button key={n} onClick={() => setPuppyCount(n)}
                    className="w-9 h-9 rounded-xl text-sm font-bold border transition-all"
                    style={{
                      background: puppyCount === n ? '#EC4899' : 'white',
                      color: puppyCount === n ? 'white' : 'var(--apisai-text-secondary)',
                      borderColor: puppyCount === n ? '#EC4899' : 'var(--apisai-border)',
                    }}
                    data-testid={`count-${n}`}>{n}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--apisai-text-secondary)' }}>Rodzice (opcjonalnie)</label>
            <input value={parents} onChange={(e) => setParents(e.target.value)}
              placeholder="np. Jeta × Kramer, championowie, badania HD/ED"
              className="w-full rounded-xl border px-3 py-2.5 text-sm"
              style={{ borderColor: 'var(--apisai-border)' }}
              data-testid="parents-input" />
          </div>

          <div className="mb-5">
            <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--apisai-text-secondary)' }}>Dodatkowe informacje (opcjonalnie)</label>
            <textarea value={additionalInfo} onChange={(e) => setAdditionalInfo(e.target.value)}
              placeholder="np. szczenięta socjalizowane, z metryką ZKwP, szczepione"
              rows={2}
              className="w-full rounded-xl border px-3 py-2.5 text-sm resize-none"
              style={{ borderColor: 'var(--apisai-border)' }}
              data-testid="info-input" />
          </div>

          <Button onClick={handleGenerate} disabled={generating}
            className="w-full rounded-xl h-12 font-semibold text-white text-sm"
            style={{ background: '#EC4899' }}
            data-testid="generate-ad-btn">
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generuję ogłoszenie...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Generuj ogłoszenie</>
            )}
          </Button>
        </motion.div>

        {/* Results */}
        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3" data-testid="ad-results">
              {/* Summary badge */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl px-5 py-3 flex items-center gap-2"
                style={{ background: '#FDF2F8', border: '1px solid #EC489930' }}>
                <Sparkles className="w-4 h-4 text-pink-500" />
                <span className="text-sm font-medium text-pink-700">
                  {result.breed} · {result.city} · {result.puppy_count} szczeniąt
                </span>
              </motion.div>

              <ResultSection icon={Facebook} title="Post Facebook" content={result.facebook_post} iconColor="#1877F2" label="Post" />
              <ResultSection icon={FileText} title="Ogłoszenie OLX / Marketplace" content={result.ogloszenie} iconColor="#F97316" label="Ogłoszenie" />
              <ResultSection icon={Megaphone} title="Call to Action" content={result.cta} iconColor="#10B981" label="CTA" />
              <ResultSection icon={Hash} title="Hashtagi" content={result.hashtagi} iconColor="#8B5CF6" label="Hashtagi" />
              <ResultSection icon={Heart} title="Opis miotu" content={result.opis_miotu} iconColor="#EC4899" label="Opis" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </PanelLayout>
  );
}
